import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'motion/react'
import { useAuthStore } from '@/stores/authStore'
import { adminAuthApi } from '@/api/auth'
import { ApiError } from '@/api/client'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CircleNotch, ArrowRight, ArrowLeft } from '@phosphor-icons/react'

type Step = 'email' | 'otp'

export function Login() {
  const navigate = useNavigate()
  const login = useAuthStore((s) => s.login)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Redirect to dashboard when already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, navigate])

  // Prevent flash of login form while redirect is in progress
  if (isAuthenticated) {
    return null
  }

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      await adminAuthApi.requestOtp(email)
      setStep('otp')
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError('Not authorized as platform administrator')
      } else {
        setError(
          err instanceof Error ? err.message : 'Failed to send verification code'
        )
      }
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const result = await login(email, otp)

      if (result.status === 'success') {
        navigate('/', { replace: true })
      } else if (result.status === 'not_admin') {
        // Store flag for access-denied page
        sessionStorage.setItem('admin-not-authorized', 'true')
        navigate('/access-denied', { replace: true })
      } else {
        setError(result.message)
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Verification failed'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleBackToEmail = () => {
    setStep('email')
    setOtp('')
    setError(null)
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <motion.div
        className="w-full max-w-sm"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        <Card className="relative overflow-hidden border-border/50 transition-shadow hover:shadow-lg hover:shadow-primary/5">
          {/* Teal gradient line at top -- control plane visual cue */}
          <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent" />

          <CardHeader className="space-y-1 pb-2 pt-8 text-center">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">
              Copilot Core
            </h1>
            <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
              Control Plane
            </p>
          </CardHeader>

          <CardContent className="pb-8 pt-4">
            {step === 'email' ? (
              <form onSubmit={handleRequestOtp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    autoComplete="email"
                    disabled={loading}
                  />
                </div>

                {error && (
                  <p className="text-sm text-destructive">{error}</p>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || !email}
                >
                  {loading ? (
                    <CircleNotch className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Send verification code
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div className="space-y-1 text-center">
                  <p className="text-sm text-muted-foreground">
                    Code sent to
                  </p>
                  <p className="font-mono text-sm text-foreground">{email}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="otp">Verification code</Label>
                  <Input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Enter 6-digit code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    autoFocus
                    autoComplete="one-time-code"
                    disabled={loading}
                    maxLength={6}
                  />
                </div>

                {error && (
                  <p className="text-sm text-destructive">{error}</p>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || !otp}
                >
                  {loading ? (
                    <CircleNotch className="h-4 w-4 animate-spin" />
                  ) : (
                    'Verify'
                  )}
                </Button>

                <button
                  type="button"
                  onClick={handleBackToEmail}
                  className="flex w-full items-center justify-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
                  disabled={loading}
                >
                  <ArrowLeft className="h-3 w-3" />
                  Use a different email
                </button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-xs text-muted-foreground/60">
          Platform administration access only
        </p>
      </motion.div>
    </div>
  )
}
